import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Pencil } from 'lucide-react'
import { useCompany, useActivateCompany, useDeactivateCompany } from '@/hooks/useCompanies'
import { StatusBadge } from '@/components/ui/status-badge'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Button } from '@/components/ui/button'
import { maskCnpj, maskPhone } from '@/components/form/masks'

function DetailRow({ label, value }: Readonly<{ label: string; value?: string | null }>) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{value || '—'}</dd>
    </div>
  )
}

export function CompanyDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { data: company, isLoading } = useCompany(id ?? '')
  const activate = useActivateCompany()
  const deactivate = useDeactivateCompany()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const isPending = activate.isPending || deactivate.isPending

  async function handleToggle() {
    if (!company) return
    try {
      if (company.active) {
        await deactivate.mutateAsync(company.id)
        toast.success('Empresa desativada')
      } else {
        await activate.mutateAsync(company.id)
        toast.success('Empresa ativada')
      }
    } catch {
      toast.error('Erro ao alterar status da empresa')
    } finally {
      setConfirmOpen(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    )
  }

  if (!company) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Empresa não encontrada.</p>
      </div>
    )
  }

  const hasAddress = company.city || company.state || company.street

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate('/companies')}>
          <ArrowLeft size={16} />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold text-foreground truncate">{company.tradeName}</h1>
            <StatusBadge active={company.active} />
          </div>
          <p className="text-sm text-muted-foreground truncate">{company.legalName}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/companies/${company.id}/edit`)}
        >
          <Pencil size={14} />
          Editar
        </Button>
      </div>

      <div className="space-y-5 rounded-lg border border-border p-5">
        <div>
          <h2 className="mb-3 text-sm font-medium text-foreground">Dados da empresa</h2>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <DetailRow label="CNPJ" value={maskCnpj(company.cnpj)} />
            <DetailRow label="Inscrição estadual" value={company.stateRegistration} />
            <DetailRow
              label="Telefone"
              value={company.phone ? maskPhone(company.phone) : null}
            />
            <DetailRow label="Email" value={company.email} />
            <DetailRow label="Contato" value={company.contactPerson} />
          </dl>
        </div>

        {hasAddress && (
          <div className="border-t border-border pt-4">
            <h2 className="mb-3 text-sm font-medium text-foreground">Endereço</h2>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <DetailRow label="CEP" value={company.zipCode} />
              <div className="col-span-2">
                <DetailRow label="Logradouro" value={company.street} />
              </div>
              <DetailRow label="Número" value={company.number} />
              <DetailRow label="Complemento" value={company.complement} />
              <DetailRow label="Bairro" value={company.neighborhood} />
              <DetailRow label="Cidade" value={company.city} />
              <DetailRow label="UF" value={company.state} />
            </dl>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          variant={company.active ? 'destructive' : 'default'}
          onClick={() => setConfirmOpen(true)}
          disabled={isPending}
        >
          {company.active ? 'Desativar empresa' : 'Ativar empresa'}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={company.active ? 'Desativar empresa?' : 'Ativar empresa?'}
        description={
          company.active
            ? `A empresa "${company.tradeName}" ficará inativa e não aparecerá em novos fretes.`
            : `A empresa "${company.tradeName}" voltará a ficar disponível.`
        }
        confirmLabel={company.active ? 'Desativar' : 'Ativar'}
        destructive={company.active}
        loading={isPending}
        onConfirm={handleToggle}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  )
}
